import { Fragment, FormEvent, ReactNode, useEffect, useState } from 'react';
import { backendApi, ApiError, tokens } from '../lib/api';
import { applyEnv } from '../lib/env';
import { useAuth } from '../lib/auth';
import { StatCard, Icon } from '../components/Icon';

/* ── types ─────────────────────────────────────────────── */
interface Trailing { ym: string; grossBurn?: number; revenue?: number; netBurn?: number }
interface ByKind { total?: number; count?: number }
interface SummaryCat { id?: string; name?: string; bucket?: string; kind?: string; amount?: number }
interface Summary {
  range?: { from?: string; to?: string };
  totals?: { spend?: number; revenue?: number; netBurn?: number; count?: number };
  unpaid?: { total?: number; count?: number; byKind?: Record<string, number> };
  byKind?: Record<string, ByKind>;
  categories?: SummaryCat[];
  intlFeeTotal?: number;
  avgNetBurn?: number; runwayMonths?: number | null; cash?: number | null;
}
interface Runway {
  fundingTotal?: number; cashRemaining?: number; monthlyBurn?: number; fixed?: number; variable?: number;
  runwayMonths?: number | null; window?: number;
  runwayActual?: number | null; runwayForecast?: number | null; runwayHeadline?: number | null; runwayBasis?: string;
  cashOnHand?: number | null; cashAsOf?: string | null; headlineCash?: number | null;
  avgNetBurn?: number; trailingMonths?: Trailing[];
}
interface Expense {
  id: string; description?: string; vendor_name?: string; category_id?: string; vendor_id?: string;
  category_name?: string; category_bucket?: string;
  amount?: number | string; native_amount?: number | string; fx_rate?: number | string; intl_fee_pct?: number | string;
  currency?: string; status?: string; spent_on?: string; attachment_count?: number | string;
  is_recurring?: boolean; recurrence?: string | null; invoice_number?: string | null;
  payment_method?: string | null; notes?: string | null;
}
interface Subscription {
  id: string; name?: string; vendor_name?: string; vendor_id?: string; category_id?: string;
  amount_usd?: number | string; native_amount?: number | string; monthly_cost_normalized?: number | string;
  currency?: string; fx_rate?: number | string; intl_fee_pct?: number | string;
  billing_interval?: string; started_on?: string; status?: string; effective_status?: string;
  next_renewal_date?: string; effective_end_date?: string;
}
interface SubSummary { monthlyRunRate?: number; annualRunRate?: number; activeCount?: number }
interface Funding { id: string; source?: string; amount?: number; received_on?: string; note?: string; notes?: string }
interface BudgetLine { id: string; label?: string; amount?: number | string; cost_type?: string; is_active?: boolean; kind?: string }
interface Category { id: string; name?: string; bucket?: string; kind?: string }
interface Vendor { id: string; name?: string; website?: string | null }

/* ── constants ─────────────────────────────────────────── */
const CURRENCIES = ['USD', 'PKR', 'EUR', 'GBP', 'AED'];
const EXPENSE_STATUS = ['draft', 'pending', 'paid'];
const SUB_INTERVALS = ['monthly', 'quarterly', 'semiannual', 'annual', 'one_time'];
const SUB_STATUSES = ['trial', 'active', 'paused'];
const RECURRENCES = ['', 'monthly', 'quarterly', 'semiannual', 'annual'];
const BUCKETS = [
  { v: 'cogs', label: 'COGS' }, { v: 'rnd', label: 'R&D' },
  { v: 'sm', label: 'S&M' }, { v: 'ga', label: 'G&A' },
];
const BUCKET_LABEL: Record<string, string> = { cogs: 'COGS', rnd: 'R&D', sm: 'S&M', ga: 'G&A' };
const BUCKET_BADGE: Record<string, string> = { cogs: 'badge-amber', rnd: 'badge-purple', sm: 'badge-green', ga: 'badge-gray' };
const KINDS: { v: string; label: string }[] = [
  { v: 'all', label: 'Total spent' }, { v: 'salary', label: 'Salaries' },
  { v: 'subscription', label: 'Subscriptions' }, { v: 'expense', label: 'Expenses' },
];
const INTL_FEE_PCT = 3;
const NSYM: Record<string, string> = { PKR: 'Rs ', EUR: '€', GBP: '£', USD: '$', AED: 'AED ' };
const SECTIONS = ['overview', 'expenses', 'subscriptions', 'reports', 'setup'] as const;
type Sec = typeof SECTIONS[number];
const SECTION_LABEL: Record<Sec, string> = {
  overview: 'Overview', expenses: 'Expenses', subscriptions: 'Subscriptions', reports: 'Reports', setup: 'Setup',
};

/* ── formatters ────────────────────────────────────────── */
const num = (n?: number | string | null) => (typeof n === 'string' ? parseFloat(n) : n);
const money = (n?: number | string | null, cur = 'USD') => {
  const v = num(n);
  return v == null || Number.isNaN(v) ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(v);
};
const moneyc = (n?: number | string | null) => {
  const v = num(n);
  return v == null || Number.isNaN(v) ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};
const fmtNative = (n?: number | string, cur = 'USD') => {
  const v = num(n); if (v == null || Number.isNaN(v)) return '';
  return (NSYM[cur] || cur + ' ') + new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v);
};
const date = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');
const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monShort = (ym?: string) => {
  const p = String(ym || '').split('-'); if (p.length < 2) return String(ym || '');
  return new Date(Number(p[0]), Number(p[1]) - 1, 1).toLocaleString('en-US', { month: 'short' });
};

/* ── auth-carrying fetch (window.open can't send the bearer) ── */
function authFetch(apiPath: string) {
  const access = tokens.access;
  return fetch(applyEnv('/api' + apiPath), { headers: access ? { Authorization: `Bearer ${access}` } : {} });
}
async function openBlob(apiPath: string, download?: string) {
  const res = await authFetch(apiPath);
  if (!res.ok) throw new ApiError('Request failed', res.status);
  const url = URL.createObjectURL(await res.blob());
  if (download) {
    const a = document.createElement('a'); a.href = url; a.download = download; a.click();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
async function openAttachment(expenseId: string) {
  const { attachments } = await backendApi.get<{ attachments: { id: string; original_name?: string }[] }>(
    `/license/admin/finances/expenses/${expenseId}/attachments`);
  const a = attachments?.[0];
  if (a) await openBlob(`/license/admin/finances/attachments/${a.id}`);
}

export default function Finances() {
  const { can } = useAuth();
  const canWrite = can('finances', 'write');
  const [section, setSection] = useState<Sec>('overview');
  const [note, setNote] = useState(''); const [error, setError] = useState('');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [runway, setRunway] = useState<Runway | null>(null);
  const [runwayWin, setRunwayWin] = useState(3);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [subSummary, setSubSummary] = useState<SubSummary>({});
  const [funding, setFunding] = useState<Funding[]>([]);
  const [fundingTotal, setFundingTotal] = useState(0);
  const [budget, setBudget] = useState<BudgetLine[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [drive, setDrive] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [modalExpense, setModalExpense] = useState<Expense | {} | null>(null);
  const [editSub, setEditSub] = useState<Subscription | null>(null);
  const [editFund, setEditFund] = useState<Funding | null>(null);

  // Expenses filters
  const [expFrom, setExpFrom] = useState(''); const [expTo, setExpTo] = useState('');
  const [expCat, setExpCat] = useState(''); const [expVen, setExpVen] = useState('');
  const [expStatus, setExpStatus] = useState(''); const [expKind, setExpKind] = useState('all');

  // Reports date range (YYYY-MM)
  const [repFrom, setRepFrom] = useState(thisMonth()); const [repTo, setRepTo] = useState(thisMonth());

  const g = <T,>(p: string, fb: T) => backendApi.get<T>(p).catch(() => fb);

  async function load() {
    const [s, r, e, sub, fu, b, cat, ven, dr] = await Promise.all([
      g<Summary>('/license/admin/finances/summary', {}),
      g<Runway>(`/license/admin/finances/runway?window=${runwayWin}`, {}),
      g<{ expenses: Expense[] }>('/license/admin/finances/expenses?limit=100', { expenses: [] }),
      g<{ subscriptions: Subscription[]; summary?: SubSummary }>('/license/admin/finances/subscriptions', { subscriptions: [] }),
      g<{ funding: Funding[]; total?: number }>('/license/admin/finances/funding', { funding: [] }),
      g<{ lines: BudgetLine[] }>('/license/admin/finances/budget', { lines: [] }),
      g<{ categories: Category[] }>('/license/admin/finances/categories', { categories: [] }),
      g<{ vendors: Vendor[] }>('/license/admin/finances/vendors', { vendors: [] }),
      g<any>('/license/admin/finances/drive/status', null),
    ]);
    setSummary(s); setRunway(r); setExpenses(e.expenses || []);
    setSubs(sub.subscriptions || []); setSubSummary(sub.summary || {});
    setFunding(fu.funding || []); setFundingTotal(Number(fu.total) || 0);
    setBudget((b as any).lines || (b as any).budget || []);
    setCategories((cat as any).categories || []);
    setVendors((ven as any).vendors || []);
    setDrive(dr); setLoading(false);
  }
  useEffect(() => {
    load().catch((err: ApiError) => { setError(err.isForbidden ? 'You do not have access to finances.' : err.message); setLoading(false); });
  }, []);

  async function reload() { await load(); }
  const done = (msg: string) => { setNote(msg); setError(''); setOpenForm(null); setModalExpense(null); setEditSub(null); setEditFund(null); void reloadExpenses(); void reload(); };
  const fail = (err: any) => setError(err.body?.error || err.message);

  async function loadRunway(win: number) {
    setRunwayWin(win);
    try { setRunway(await backendApi.get<Runway>(`/license/admin/finances/runway?window=${win}`)); } catch { /* keep prior */ }
  }

  async function reloadExpenses() {
    const qs = new URLSearchParams({ limit: '200' });
    if (expFrom) qs.set('from', expFrom); if (expTo) qs.set('to', expTo);
    if (expCat) qs.set('category', expCat); if (expVen) qs.set('vendor', expVen);
    if (expStatus) qs.set('status', expStatus); if (expKind && expKind !== 'all') qs.set('kind', expKind);
    try {
      const [e, s] = await Promise.all([
        backendApi.get<{ expenses: Expense[] }>(`/license/admin/finances/expenses?${qs}`),
        (expFrom || expTo)
          ? backendApi.get<Summary>(`/license/admin/finances/summary?${new URLSearchParams({ ...(expFrom ? { from: expFrom } : {}), ...(expTo ? { to: expTo } : {}) })}`).catch(() => null)
          : Promise.resolve(null),
      ]);
      setExpenses(e.expenses || []); if (s) setSummary(s);
    } catch (err) { fail(err); }
  }
  // refetch when a filter changes while on the expenses tab
  useEffect(() => { if (!loading && section === 'expenses') void reloadExpenses(); /* eslint-disable-next-line */ }, [expFrom, expTo, expCat, expVen, expStatus, expKind]);

  async function setStatus(e: Expense, status: string) {
    setError(''); setNote('');
    try { await backendApi.put(`/license/admin/finances/expenses/${e.id}`, { status }); setNote(`Marked ${e.description || 'expense'} ${status}.`); await reloadExpenses(); await reload(); }
    catch (err) { fail(err); }
  }
  async function voidExpense(e: Expense) {
    if (!confirm(`Void "${e.description || e.id}"?`)) return;
    try { await backendApi.delete(`/license/admin/finances/expenses/${e.id}`); done('Expense voided.'); } catch (err) { fail(err); }
  }
  async function delBudget(b: BudgetLine) {
    if (!confirm(`Delete budget line "${b.label}"?`)) return;
    try { await backendApi.delete(`/license/admin/finances/budget/${b.id}`); done('Budget line deleted.'); } catch (err) { fail(err); }
  }
  async function saveBudgetAmount(b: BudgetLine, amount: string) {
    if (amount === '' || Number(amount) === Number(b.amount)) return;
    try { await backendApi.put(`/license/admin/finances/budget/${b.id}`, { amount }); setNote('Budget updated.'); await reload(); } catch (err) { fail(err); }
  }
  async function delFunding(f: Funding) {
    if (!confirm(`Delete funding "${f.source}"?`)) return;
    try { await backendApi.delete(`/license/admin/finances/funding/${f.id}`); done('Funding entry deleted.'); } catch (err) { fail(err); }
  }
  async function subAction(s: Subscription, verb: 'pause' | 'resume' | 'cancel') {
    if (verb === 'cancel' && !confirm(`Cancel subscription "${s.name}"?`)) return;
    try { await backendApi.post(`/license/admin/finances/subscriptions/${s.id}/${verb}`, {}); done(`Subscription ${verb}d.`); } catch (err) { fail(err); }
  }
  async function delSub(s: Subscription) {
    if (!confirm(`Delete subscription "${s.name}"? This removes it from history.`)) return;
    try { await backendApi.delete(`/license/admin/finances/subscriptions/${s.id}`); done('Subscription deleted.'); } catch (err) { fail(err); }
  }
  async function viewReceipt(e: Expense) {
    setError('');
    try { await openAttachment(e.id); } catch (err) { fail(err); }
  }
  async function report(kind: 'export' | 'statement' | 'forecast') {
    setError('');
    try {
      if (kind === 'export') await openBlob(`/license/admin/finances/export?from=${repFrom}&to=${repTo}`, `finances-${repFrom}-to-${repTo}.csv`);
      else if (kind === 'statement') await openBlob(`/license/admin/finances/statement?from=${repFrom}&to=${repTo}`);
      else await openBlob(`/license/admin/finances/forecast?month=${repTo}`);
    } catch (err) { fail(err); }
  }

  const subsMonthly = Number(subSummary.monthlyRunRate) || subs.reduce((s, x) => s + (Number(x.monthly_cost_normalized) || 0), 0);
  const rw = runway || {};
  const months = ('runwayHeadline' in rw) ? rw.runwayHeadline : rw.runwayActual;
  const cash = rw.headlineCash != null ? rw.headlineCash : (rw.cashOnHand ?? summary?.cash ?? rw.cashRemaining);
  const burn = rw.avgNetBurn ?? summary?.avgNetBurn ?? rw.monthlyBurn;
  const runwayMo = months ?? rw.runwayMonths ?? summary?.runwayMonths;

  const toggle = (k: string) => setOpenForm(openForm === k ? null : k);

  return (
    <>
      <header className="page-head"><div><h1>Finances</h1><p className="muted">Cash, burn, runway and spend.</p></div></header>

      <div className="stat-row">
        <StatCard icon="dollar" tint="green" value={money(cash)} label="Cash on hand" />
        <StatCard icon="trending" tint="amber" value={money(burn)} label="Net burn / mo"
          sub={runwayMo != null ? `${Number(runwayMo).toFixed(1)} months runway` : undefined} />
        <StatCard icon="chart" tint="purple" value={money(rw.fundingTotal)} label="Funding raised" />
        <StatCard icon="tag" tint="gray" value={money(subsMonthly)} label="Subscriptions / mo" sub={`${money(subsMonthly * 12)} annualized`} />
      </div>

      <div className="seg" style={{ marginBottom: 16 }}>
        {SECTIONS.map((s) => (
          <button key={s} className={'seg-btn' + (section === s ? ' on' : '')} onClick={() => setSection(s)}>{SECTION_LABEL[s]}</button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note}</div>}

      {loading ? <p className="muted">Loading…</p> : (
        <>
          {section === 'overview' && (
            <OverviewPane runway={runway} summary={summary} win={runwayWin} onWin={loadRunway}
              funding={funding} fundingTotal={fundingTotal} budget={budget} canWrite={canWrite}
              openForm={openForm} toggle={toggle} done={done} fail={fail}
              editFund={editFund} setEditFund={setEditFund} delFunding={delFunding}
              saveBudgetAmount={saveBudgetAmount} delBudget={delBudget} />
          )}

          {section === 'expenses' && (
            <ExpensesPane expenses={expenses} summary={summary} categories={categories} vendors={vendors}
              canWrite={canWrite}
              filters={{ from: expFrom, to: expTo, cat: expCat, ven: expVen, status: expStatus, kind: expKind }}
              setFrom={setExpFrom} setTo={setExpTo} setCat={setExpCat} setVen={setExpVen} setStatusF={setExpStatus} setKind={setExpKind}
              onAdd={() => setModalExpense({})} onEdit={(e) => setModalExpense(e)}
              setStatus={setStatus} voidExpense={voidExpense} viewReceipt={viewReceipt} />
          )}

          {section === 'subscriptions' && (
            <SubscriptionsPane subs={subs} summary={subSummary} categories={categories} vendors={vendors}
              canWrite={canWrite} openForm={openForm} toggle={toggle} done={done} fail={fail}
              editSub={editSub} setEditSub={setEditSub} subAction={subAction} delSub={delSub} />
          )}

          {section === 'reports' && (
            <ReportsPane from={repFrom} to={repTo} setFrom={setRepFrom} setTo={setRepTo} run={report} />
          )}

          {section === 'setup' && (
            <SetupPane categories={categories} vendors={vendors} canWrite={canWrite} done={done} fail={fail} drive={drive} />
          )}

          {modalExpense !== null && (
            <ExpenseModal expense={modalExpense} categories={categories} vendors={vendors}
              onClose={() => setModalExpense(null)} onDone={done} onError={fail} onView={viewReceipt} />
          )}
        </>
      )}
    </>
  );
}

/* ══════════════════════════ OVERVIEW ══════════════════════════ */
function OverviewPane({ runway, summary, win, onWin, funding, fundingTotal, budget, canWrite, openForm, toggle, done, fail, editFund, setEditFund, delFunding, saveBudgetAmount, delBudget }: {
  runway: Runway | null; summary: Summary | null; win: number; onWin: (w: number) => void;
  funding: Funding[]; fundingTotal: number; budget: BudgetLine[]; canWrite: boolean;
  openForm: string | null; toggle: (k: string) => void; done: (m: string) => void; fail: (e: any) => void;
  editFund: Funding | null; setEditFund: (f: Funding | null) => void; delFunding: (f: Funding) => void;
  saveBudgetAmount: (b: BudgetLine, a: string) => void; delBudget: (b: BudgetLine) => void;
}) {
  const rw = runway || {};
  const avgNetBurn = Number(rw.avgNetBurn) || 0;
  const months = ('runwayHeadline' in rw) ? rw.runwayHeadline : rw.runwayActual;
  const basis = rw.runwayBasis || (rw.runwayActual != null ? 'cash' : 'funding');

  // Runway hero
  let heroVal: ReactNode, chipCls = '', chipLabel = '', heroSub = '';
  if (months == null && avgNetBurn <= 0) {
    heroVal = 'Not burning cash'; chipCls = 'good'; chipLabel = 'healthy';
    heroSub = 'Spending is covered by revenue this period.';
  } else if (months == null) {
    heroVal = 'Runway —'; heroSub = 'Add this month’s cash balance to see your runway.';
  } else {
    const d = new Date(); d.setMonth(d.getMonth() + Math.round(months));
    const when = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    heroVal = <>Cash-out <strong>~{when}</strong> · {months} month{Number(months) === 1 ? '' : 's'} left</>;
    if (months < 6) { chipCls = 'bad'; chipLabel = 'low'; }
    else if (months < 18) { chipCls = 'warn'; chipLabel = 'watch'; }
    else { chipCls = 'good'; chipLabel = 'healthy'; }
    heroSub = basis === 'funding'
      ? 'Based on investor funding − spend. Enter this month’s reconciled cash for a bank-accurate date.'
      : `Based on reconciled cash ÷ net burn${rw.cashAsOf ? ' · cash as of ' + rw.cashAsOf : ''}.`;
  }
  const chipStyle = chipCls === 'bad' ? { background: 'rgba(239,68,68,0.14)', color: '#EF4444' } : undefined;
  const chipBadge = chipCls === 'good' ? 'badge-green' : chipCls === 'warn' ? 'badge-amber' : 'badge-gray';

  // sparkline
  const tm = (rw.trailingMonths || []).slice(-6);
  const nets = tm.map((m) => Math.max(0, Number(m.netBurn) || 0));
  const maxNet = Math.max(...nets, 1);
  const avg = (k: keyof Trailing) => tm.length ? tm.reduce((a, m) => a + (Number(m[k]) || 0), 0) / tm.length : 0;

  // owed pill + spend rollup
  const unpaid = summary?.unpaid;
  const bk = summary?.byKind || {};
  const rollup = [
    { label: 'Salaries', val: Number(bk.salary?.total) || 0 },
    { label: 'Subscriptions', val: Number(bk.subscription?.total) || 0 },
    { label: 'Expenses', val: Number(bk.expense?.total) || 0 },
  ];
  const rollTotal = rollup.reduce((a, b) => a + b.val, 0);

  // per-category breakdown
  const cats = [...(summary?.categories || [])].sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0));
  const catTotal = cats.reduce((a, c) => a + (Number(c.amount) || 0), 0);

  return (
    <>
      <div className="card">
        <div className="role-head">
          <h2 className="card-title" style={{ margin: 0 }}>Runway</h2>
          <div className="seg">
            {[1, 3, 6].map((w) => (
              <button key={w} className={'seg-btn' + (win === w ? ' on' : '')} onClick={() => onWin(w)}>{w} mo</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{heroVal}</div>
          {chipLabel && <span className={'badge ' + chipBadge} style={chipStyle}>{chipLabel}</span>}
        </div>
        <p className="muted small" style={{ marginTop: 6 }}>{heroSub}</p>

        {/* net-burn sparkline */}
        {tm.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="spark-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Monthly net burn</span>
              <span className="muted small">avg <strong>{money(rw.avgNetBurn || 0)}</strong>/mo · gross {money(avg('grossBurn'))} · revenue {money(avg('revenue'))}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 90 }}>
              {tm.map((m, i) => {
                const last = i === tm.length - 1;
                const h = Math.round(nets[i] / maxNet * 100);
                const nb = Number(m.netBurn) || 0;
                return (
                  <div key={m.ym} title={`${monShort(m.ym)} ${String(m.ym).split('-')[0]} · net burn ${money(nb)}${nb <= 0 ? ' (surplus)' : ''}`}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end' }}>
                      <div style={{ width: '100%', height: `${Math.max(h, 2)}%`, borderRadius: 3,
                        background: last ? 'var(--color-status-warning-accent)' : 'var(--color-surface-overlay)' }} />
                    </div>
                    <span className="muted" style={{ fontSize: 10 }}>{monShort(m.ym)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* forecast + owed */}
        <p className="muted small" style={{ marginTop: 14 }}>
          Forecast (planning): {rw.runwayForecast == null ? 'n/a' : rw.runwayForecast + ' mo'} — funding − spend {money(rw.cashRemaining || 0)} ÷ budget burn {money(rw.monthlyBurn || 0)}/mo
        </p>
        {unpaid && Number(unpaid.total) > 0 && (
          <span className="badge badge-amber" style={{ marginTop: 4 }}>
            Owed / unpaid: {moneyc(unpaid.total)} · {unpaid.count} item{Number(unpaid.count) === 1 ? '' : 's'} — not counted in burn
          </span>
        )}
      </div>

      {/* spend rollup + category breakdown */}
      <div className="card">
        <h2 className="card-title">Spend this period</h2>
        {rollTotal === 0 ? <p className="muted">No paid spend recorded this month.</p> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rollup.map((r) => {
              const pct = rollTotal ? Math.round(r.val / rollTotal * 100) : 0;
              return (
                <div key={r.label} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 60px 90px', alignItems: 'center', gap: 10 }}>
                  <span className="small">{r.label}</span>
                  <div style={{ background: 'var(--color-surface-sunken)', borderRadius: 4, height: 8 }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: 'var(--color-status-info-fg)' }} />
                  </div>
                  <span className="muted small right">{pct}%</span>
                  <span className="mono right">{money(r.val)}</span>
                </div>
              );
            })}
          </div>
        )}
        {cats.length > 0 && (
          <>
            <h3 className="card-title" style={{ fontSize: 13, marginTop: 18 }}>By category</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cats.map((c, i) => {
                const pct = catTotal ? Math.round((Number(c.amount) || 0) / catTotal * 100) : 0;
                return (
                  <div key={c.id || i} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 140px', alignItems: 'center', gap: 10 }}>
                    <span className="small">{c.name} {c.bucket && <span className={'badge ' + (BUCKET_BADGE[c.bucket] || 'badge-gray')}>{BUCKET_LABEL[c.bucket] || c.bucket}</span>}</span>
                    <div style={{ background: 'var(--color-surface-sunken)', borderRadius: 4, height: 6 }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: 'var(--color-text-tertiary)' }} />
                    </div>
                    <span className="mono small right">{moneyc(c.amount)} <em className="muted">{pct}%</em></span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Cash & funding */}
      <Section title={`Cash & funding (${funding.length})`}
        action={canWrite && <div className="row-actions">
          <button className="btn btn-sm" onClick={() => toggle('cash')}>{openForm === 'cash' ? 'Cancel' : 'Update cash'}</button>
          <button className="btn btn-sm" onClick={() => toggle('funding')}>{openForm === 'funding' ? 'Cancel' : '+ Add funding'}</button>
        </div>}>
        {openForm === 'cash' && <CashForm onDone={done} onError={fail} />}
        {(openForm === 'funding' || editFund) && <FundingForm initial={editFund || undefined} onDone={done} onError={fail} onCancel={() => setEditFund(null)} />}
        <Table cols={['Date', 'Source', 'Amount', 'Notes', canWrite ? 'Actions' : '']}
          rows={funding} empty="No funding entries."
          render={(f: Funding) => (
            <tr key={f.id}>
              <td className="muted">{date(f.received_on)}</td>
              <td>{f.source || '—'}</td>
              <td className="mono">{moneyc(f.amount)}</td>
              <td className="muted">{f.note ?? f.notes ?? '—'}</td>
              {canWrite && <td className="right"><div className="row-actions">
                <button className="btn-icon" title="Edit" onClick={() => setEditFund(f)}><Icon name="pencil" size={15} /></button>
                <button className="btn-icon danger" title="Delete" onClick={() => delFunding(f)}><Icon name="trash" size={15} /></button>
              </div></td>}
            </tr>
          )}
          foot={funding.length > 0 && (
            <tr><td colSpan={2}><strong>Total raised</strong></td><td className="mono"><strong>{moneyc(fundingTotal)}</strong></td><td colSpan={canWrite ? 2 : 1} /></tr>
          )} />
      </Section>

      {/* Budget */}
      <BudgetCard budget={budget} canWrite={canWrite} openForm={openForm} toggle={toggle} done={done} fail={fail}
        saveBudgetAmount={saveBudgetAmount} delBudget={delBudget} />
    </>
  );
}

function BudgetCard({ budget, canWrite, openForm, toggle, done, fail, saveBudgetAmount, delBudget }: {
  budget: BudgetLine[]; canWrite: boolean; openForm: string | null; toggle: (k: string) => void;
  done: (m: string) => void; fail: (e: any) => void; saveBudgetAmount: (b: BudgetLine, a: string) => void; delBudget: (b: BudgetLine) => void;
}) {
  const groups: { type: string; label: string }[] = [{ type: 'fixed', label: 'Fixed' }, { type: 'variable', label: 'Variable' }];
  const sum = (t: string) => budget.filter((b) => (b.cost_type || 'fixed') === t).reduce((a, b) => a + (Number(b.amount) || 0), 0);
  const grand = budget.reduce((a, b) => a + (Number(b.amount) || 0), 0);
  return (
    <Section title={`Budget (${budget.length})`}
      action={canWrite && <button className="btn btn-sm" onClick={() => toggle('budget')}>{openForm === 'budget' ? 'Cancel' : '+ Add line'}</button>}>
      {openForm === 'budget' && <BudgetForm onDone={done} onError={fail} />}
      {budget.length === 0 ? <p className="muted">No budget lines.</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Line item</th><th>Kind</th><th>Amount / mo</th>{canWrite && <th className="right">Actions</th>}</tr></thead>
            <tbody>
              {groups.map((grp) => {
                const rows = budget.filter((b) => (b.cost_type || 'fixed') === grp.type);
                if (!rows.length) return null;
                return (
                  <Fragment key={grp.type}>
                    <tr><td colSpan={canWrite ? 4 : 3} className="muted small" style={{ fontWeight: 600 }}>{grp.label}</td></tr>
                    {rows.map((b) => (
                      <tr key={b.id}>
                        <td>{b.label || '—'}</td>
                        <td><span className={'badge ' + (b.kind === 'salary' ? 'badge-purple' : 'badge-gray')}>{b.kind || 'other'}</span></td>
                        <td>{canWrite
                          ? <input className="bud-amt" type="number" min="0" step="0.01" defaultValue={Number(b.amount) || 0}
                            onBlur={(ev) => saveBudgetAmount(b, ev.target.value)}
                            style={{ width: 110, padding: '4px 8px', background: 'var(--color-surface-sunken)', border: '1px solid var(--color-border-default)', borderRadius: 6, color: 'inherit', fontFamily: 'var(--font-family-mono)' }} />
                          : <span className="mono">{money(b.amount)}</span>}</td>
                        {canWrite && <td className="right"><button className="btn-icon danger" title="Delete" onClick={() => delBudget(b)}><Icon name="trash" size={15} /></button></td>}
                      </tr>
                    ))}
                    <tr className="muted small"><td>Subtotal {grp.label}</td><td /><td className="mono">{money(sum(grp.type))}</td>{canWrite && <td />}</tr>
                  </Fragment>
                );
              })}
              <tr><td colSpan={2}><strong>Expected monthly burn</strong></td><td className="mono"><strong>{money(grand)}</strong></td>{canWrite && <td />}</tr>
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ══════════════════════════ EXPENSES ══════════════════════════ */
function ExpensesPane({ expenses, summary, categories, vendors, canWrite, filters, setFrom, setTo, setCat, setVen, setStatusF, setKind, onAdd, onEdit, setStatus, voidExpense, viewReceipt }: {
  expenses: Expense[]; summary: Summary | null; categories: Category[]; vendors: Vendor[]; canWrite: boolean;
  filters: { from: string; to: string; cat: string; ven: string; status: string; kind: string };
  setFrom: (v: string) => void; setTo: (v: string) => void; setCat: (v: string) => void; setVen: (v: string) => void; setStatusF: (v: string) => void; setKind: (v: string) => void;
  onAdd: () => void; onEdit: (e: Expense) => void; setStatus: (e: Expense, s: string) => void; voidExpense: (e: Expense) => void; viewReceipt: (e: Expense) => void;
}) {
  const bk = summary?.byKind || {};
  const totals = summary?.totals || {};
  const kindTotal = (k: string) => k === 'all' ? (Number(totals.spend) || 0) : (Number(bk[k]?.total) || 0);
  return (
    <Section title={`Expenses (${expenses.length})`}
      action={canWrite && <button className="btn btn-sm btn-primary" onClick={onAdd}>+ Add expense</button>}>
      {/* spend-kind chips */}
      <div className="seg" style={{ marginBottom: 12 }}>
        {KINDS.map((k) => (
          <button key={k.v} className={'seg-btn' + (filters.kind === k.v ? ' on' : '')} onClick={() => setKind(k.v)}>
            {k.label} <span className="mono" style={{ opacity: 0.7 }}>{money(kindTotal(k.v))}</span>
          </button>
        ))}
      </div>
      {/* filter bar */}
      <div className="filter-bar" style={{ marginBottom: 14 }}>
        <label className="muted small">From <input type="date" value={filters.from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="muted small">To <input type="date" value={filters.to} onChange={(e) => setTo(e.target.value)} /></label>
        <select value={filters.cat} onChange={(e) => setCat(e.target.value)}><option value="">All categories</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <select value={filters.ven} onChange={(e) => setVen(e.target.value)}><option value="">All vendors</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        <select value={filters.status} onChange={(e) => setStatusF(e.target.value)}><option value="">Any status</option>{EXPENSE_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        {(filters.from || filters.to || filters.cat || filters.ven || filters.status) &&
          <button className="btn btn-sm btn-ghost" onClick={() => { setFrom(''); setTo(''); setCat(''); setVen(''); setStatusF(''); }}>Clear</button>}
      </div>
      <Table cols={['Date', 'Description', 'Category', 'Vendor', 'Amount', 'Status', 'Receipt', canWrite ? 'Actions' : '']}
        rows={expenses} empty="No expenses match."
        render={(e: Expense) => {
          const nonUsd = e.currency && e.currency !== 'USD';
          return (
            <tr key={e.id}>
              <td className="muted">{date(e.spent_on)}</td>
              <td>
                {e.description || '—'}
                {e.is_recurring && <span className="badge badge-gray" style={{ marginLeft: 6 }}>⟳ {e.recurrence || 'recurring'}</span>}
              </td>
              <td><span className={'badge ' + (BUCKET_BADGE[e.category_bucket || 'ga'] || 'badge-gray')}>{BUCKET_LABEL[e.category_bucket || 'ga']}</span> <span className="muted small">{e.category_name || '—'}</span></td>
              <td className="muted">{e.vendor_name || '—'}</td>
              <td className="mono">
                {money(e.amount, 'USD')}
                {nonUsd && <div className="muted" style={{ fontSize: 11 }}>{fmtNative(e.native_amount, e.currency)} @ {Number(e.fx_rate)}</div>}
                {Number(e.intl_fee_pct) > 0 && <div className="muted" style={{ fontSize: 11 }}>incl. {Number(e.intl_fee_pct)}% intl fee</div>}
              </td>
              <td><span className={'badge ' + (e.status === 'paid' ? 'badge-green' : e.status === 'draft' ? 'badge-gray' : 'badge-amber')}>{e.status || '—'}</span></td>
              <td>{Number(e.attachment_count) > 0 ? <button className="link-pill" onClick={() => viewReceipt(e)}>View</button> : <span className="muted">—</span>}</td>
              {canWrite && <td><div className="row-actions">
                <button className="btn-icon" title="Edit" onClick={() => onEdit(e)}><Icon name="pencil" size={15} /></button>
                <button className="btn btn-sm" title="Toggle paid" onClick={() => setStatus(e, e.status === 'paid' ? 'pending' : 'paid')}>{e.status === 'paid' ? 'Unpay' : 'Mark paid'}</button>
                <button className="btn-icon danger" title="Void" onClick={() => voidExpense(e)}><Icon name="trash" size={15} /></button>
              </div></td>}
            </tr>
          );
        }} />
    </Section>
  );
}

/* ══════════════════════════ SUBSCRIPTIONS ══════════════════════════ */
function SubscriptionsPane({ subs, summary, categories, vendors, canWrite, openForm, toggle, done, fail, editSub, setEditSub, subAction, delSub }: {
  subs: Subscription[]; summary: SubSummary; categories: Category[]; vendors: Vendor[]; canWrite: boolean;
  openForm: string | null; toggle: (k: string) => void; done: (m: string) => void; fail: (e: any) => void;
  editSub: Subscription | null; setEditSub: (s: Subscription | null) => void;
  subAction: (s: Subscription, v: 'pause' | 'resume' | 'cancel') => void; delSub: (s: Subscription) => void;
}) {
  return (
    <>
      <div className="card">
        <h2 className="card-title">Subscription run-rate</h2>
        <div className="stat-row" style={{ marginBottom: 0 }}>
          <StatCard icon="tag" tint="purple" value={money(summary.monthlyRunRate)} label="Monthly run-rate" />
          <StatCard icon="calendar" tint="amber" value={money(summary.annualRunRate)} label="Annual run-rate" />
          <StatCard icon="check" tint="green" value={String(summary.activeCount ?? subs.filter((s) => (s.effective_status || s.status) === 'active').length)} label="Active" />
        </div>
      </div>
      <Section title={`Subscriptions (${subs.length})`}
        action={canWrite && <button className="btn btn-sm" onClick={() => { setEditSub(null); toggle('sub'); }}>{openForm === 'sub' ? 'Cancel' : '+ Add subscription'}</button>}>
        {(openForm === 'sub' || editSub) && <SubForm initial={editSub || undefined} categories={categories} vendors={vendors} onDone={done} onError={fail} onCancel={() => setEditSub(null)} />}
        <Table cols={['Name', 'Vendor', 'Amount', 'Run-rate / mo', 'Next renewal', 'Status', canWrite ? 'Actions' : '']}
          rows={subs} empty="No subscriptions."
          render={(s: Subscription) => {
            const st = s.effective_status || s.status;
            const nonUsd = s.currency && s.currency !== 'USD';
            return (
              <tr key={s.id}>
                <td>{s.name || '—'}</td>
                <td className="muted">{s.vendor_name || '—'}</td>
                <td className="mono">
                  {money(s.amount_usd, 'USD')}
                  {nonUsd && <div className="muted" style={{ fontSize: 11 }}>{fmtNative(s.native_amount, s.currency)} @ {Number(s.fx_rate)}</div>}
                  {s.billing_interval && <span className="muted small"> /{s.billing_interval.replace('_', ' ')}</span>}
                </td>
                <td className="mono">{money(s.monthly_cost_normalized)}</td>
                <td className="muted">{date(s.next_renewal_date)}</td>
                <td><span className={'badge ' + (st === 'active' ? 'badge-green' : st === 'paused' || st === 'trial' || st === 'cancelling' ? 'badge-amber' : 'badge-gray')}>{st || '—'}</span></td>
                {canWrite && <td><div className="row-actions">
                  <button className="btn-icon" title="Edit" onClick={() => { setEditSub(s); }}><Icon name="pencil" size={15} /></button>
                  {st === 'paused'
                    ? <button className="btn btn-sm" onClick={() => subAction(s, 'resume')}>Resume</button>
                    : <button className="btn btn-sm" onClick={() => subAction(s, 'pause')}>Pause</button>}
                  <button className="btn btn-sm btn-danger" onClick={() => subAction(s, 'cancel')}>Cancel</button>
                  <button className="btn-icon danger" title="Delete" onClick={() => delSub(s)}><Icon name="trash" size={15} /></button>
                </div></td>}
              </tr>
            );
          }} />
      </Section>
    </>
  );
}

/* ══════════════════════════ REPORTS ══════════════════════════ */
function ReportsPane({ from, to, setFrom, setTo, run }: {
  from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void;
  run: (k: 'export' | 'statement' | 'forecast') => void;
}) {
  return (
    <div className="card">
      <h2 className="card-title">Reports</h2>
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <label className="muted small">From <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="muted small">To <input type="month" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      <div className="row-actions">
        <button className="btn btn-sm" onClick={() => run('export')}><Icon name="download" size={15} /> Export CSV</button>
        <button className="btn btn-sm" onClick={() => run('statement')}><Icon name="chart" size={15} /> Expenditure statement</button>
        <button className="btn btn-sm" onClick={() => run('forecast')}><Icon name="trending" size={15} /> Expected expenditure</button>
      </div>
      <p className="muted small" style={{ marginTop: 12 }}>CSV covers the From–To range. Statement uses the range; forecast uses the To month.</p>
    </div>
  );
}

/* ══════════════════════════ SETUP ══════════════════════════ */
function SetupPane({ categories, vendors, canWrite, done, fail, drive }: {
  categories: Category[]; vendors: Vendor[]; canWrite: boolean; done: (m: string) => void; fail: (e: any) => void; drive: any;
}) {
  const [catName, setCatName] = useState(''); const [catBucket, setCatBucket] = useState('ga');
  const [venName, setVenName] = useState(''); const [venSite, setVenSite] = useState('');
  const [busy, setBusy] = useState(false);
  async function addCat(e: FormEvent) {
    e.preventDefault(); if (!catName.trim()) return; setBusy(true);
    try { await backendApi.post('/license/admin/finances/categories', { name: catName.trim(), bucket: catBucket }); setCatName(''); done('Category added.'); }
    catch (err) { fail(err); } finally { setBusy(false); }
  }
  async function addVen(e: FormEvent) {
    e.preventDefault(); if (!venName.trim()) return; setBusy(true);
    try { await backendApi.post('/license/admin/finances/vendors', { name: venName.trim(), website: venSite.trim() || null }); setVenName(''); setVenSite(''); done('Vendor added.'); }
    catch (err) { fail(err); } finally { setBusy(false); }
  }
  return (
    <>
      <div className="card">
        <h2 className="card-title">Categories ({categories.length})</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: canWrite ? 14 : 0 }}>
          {categories.length === 0 ? <span className="muted">None yet.</span> : categories.map((c) => (
            <span key={c.id} className="badge badge-gray">{c.name} · <span className={'badge ' + (BUCKET_BADGE[c.bucket || 'ga'] || 'badge-gray')}>{BUCKET_LABEL[c.bucket || 'ga']}</span></span>
          ))}
        </div>
        {canWrite && (
          <form className="inline-form" onSubmit={addCat}>
            <div className="form-row">
              <label className="field"><span>Name *</span><input required value={catName} onChange={(e) => setCatName(e.target.value)} /></label>
              <label className="field"><span>Bucket</span>
                <select value={catBucket} onChange={(e) => setCatBucket(e.target.value)}>{BUCKETS.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}</select></label>
            </div>
            <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Add category'}</button>
          </form>
        )}
      </div>
      <div className="card">
        <h2 className="card-title">Vendors ({vendors.length})</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: canWrite ? 14 : 0 }}>
          {vendors.length === 0 ? <span className="muted">None yet.</span> : vendors.map((v) => (
            <span key={v.id} className="badge badge-gray">{v.name}{v.website ? ' ↗' : ''}</span>
          ))}
        </div>
        {canWrite && (
          <form className="inline-form" onSubmit={addVen}>
            <div className="form-row">
              <label className="field"><span>Name *</span><input required value={venName} onChange={(e) => setVenName(e.target.value)} /></label>
              <label className="field"><span>Website</span><input value={venSite} onChange={(e) => setVenSite(e.target.value)} placeholder="https://" /></label>
            </div>
            <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Add vendor'}</button>
          </form>
        )}
      </div>
      <div className="card">
        <h2 className="card-title">Google Drive invoice sync</h2>
        {!drive ? <p className="muted">Status unavailable.</p> : (
          <p className="muted">{drive.connected ? <>Connected{drive.email ? ` as ${drive.email}` : ''}{drive.folderName ? ` · folder: ${drive.folderName}` : ''}</> : 'Not connected.'} <span className="small">(connect/sync managed in the legacy panel)</span></p>
        )}
      </div>
    </>
  );
}

/* ══════════════════════════ shared bits ══════════════════════════ */
function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="card">
      <div className="role-head"><h2 className="card-title" style={{ margin: 0 }}>{title}</h2>{action}</div>
      {children}
    </div>
  );
}
function Table<T>({ cols, rows, empty, render, foot }: { cols: string[]; rows: T[]; empty: string; render: (r: T) => ReactNode; foot?: ReactNode }) {
  if (rows.length === 0) return <p className="muted">{empty}</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr>{cols.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>{rows.map(render)}{foot}</tbody>
      </table>
    </div>
  );
}
function useForm<T extends object>(init: T) {
  const [f, setF] = useState<T>(init);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof T) => (e: any) => setF({ ...f, [k]: e.target.value });
  return { f, set, busy, setBusy };
}

/* ══════════════════════════ EXPENSE MODAL ══════════════════════════ */
function ExpenseModal({ expense, categories, vendors, onClose, onDone, onError, onView }: {
  expense: Expense | {}; categories: Category[]; vendors: Vendor[];
  onClose: () => void; onDone: (m: string) => void; onError: (e: any) => void; onView: (e: Expense) => void;
}) {
  const e = expense as Expense;
  const editing = !!e.id;
  const seedCur = e.currency || 'USD';
  const seedAmount = seedCur !== 'USD' && e.native_amount != null ? e.native_amount : (e.amount ?? '');
  const { f, set, busy, setBusy } = useForm({
    spent_on: e.spent_on ? String(e.spent_on).slice(0, 10) : today(),
    currency: seedCur,
    amount: seedAmount === '' ? '' : String(seedAmount),
    fx_rate: e.fx_rate != null ? String(e.fx_rate) : '',
    intl: Number(e.intl_fee_pct) > 0,
    description: e.description || '',
    category_id: e.category_id || '',
    vendor_id: e.vendor_id || '',
    status: e.status || 'paid',
    recurrence: e.recurrence || '',
    invoice_number: e.invoice_number || '',
    payment_method: e.payment_method || '',
    notes: e.notes || '',
  });
  const isUsd = f.currency === 'USD';
  const feePct = f.intl ? INTL_FEE_PCT : 0;
  const amt = Number(f.amount) || 0;
  const fx = Number(f.fx_rate) || 0;
  const baseUsd = isUsd ? amt : (fx > 0 ? amt / fx : 0);
  const total = Math.round(baseUsd * (1 + feePct / 100) * 100) / 100;

  async function submit(ev: FormEvent) {
    ev.preventDefault(); setBusy(true);
    const body: any = {
      spent_on: f.spent_on, currency: f.currency, description: f.description.trim(),
      category_id: f.category_id || null, vendor_id: f.vendor_id || null, status: f.status,
      is_recurring: f.recurrence !== '', recurrence: f.recurrence || null,
      invoice_number: f.invoice_number || null, payment_method: f.payment_method || null,
      notes: f.notes || null, intl_fee_pct: f.intl ? INTL_FEE_PCT : 0,
    };
    if (isUsd) { body.amount = f.amount; }
    else {
      if (!(fx > 0) || fx === 1) { onError({ message: `Enter the exchange rate for ${f.currency} (units per 1 USD, e.g. 280 for PKR).` }); setBusy(false); return; }
      body.native_amount = f.amount; body.fx_rate = f.fx_rate;
    }
    try {
      if (editing) await backendApi.put(`/license/admin/finances/expenses/${e.id}`, body);
      else await backendApi.post('/license/admin/finances/expenses', body);
      onDone(editing ? 'Expense updated.' : 'Expense added.');
    } catch (err) { onError(err); } finally { setBusy(false); }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-head">
          <h2 className="card-title" style={{ margin: 0 }}>{editing ? 'Edit expense' : 'Add expense'}</h2>
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
        </div>
        <form onSubmit={submit}>
          <div className="form-row">
            <label className="field"><span>Date *</span><input required type="date" value={f.spent_on} onChange={set('spent_on')} /></label>
            <label className="field"><span>Currency</span>
              <select value={f.currency} onChange={set('currency')}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
            <label className="field"><span>{isUsd ? 'Amount (USD) *' : `Amount (${f.currency}) *`}</span>
              <input required type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} /></label>
            {!isUsd && <label className="field"><span>FX rate ({f.currency}/USD) *</span>
              <input type="number" min="0" step="0.0001" value={f.fx_rate} onChange={set('fx_rate')} placeholder="e.g. 280" /></label>}
          </div>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={f.intl} onChange={(ev) => set('intl')({ target: { value: ev.target.checked } })} />
            <span>International card / forex fee ({INTL_FEE_PCT}%)</span>
          </label>
          {(amt > 0) && <p className="muted small">{moneyc(baseUsd)}{!isUsd && ` at ${fx || '?'} ${f.currency}/USD`}{feePct > 0 && ` + ${moneyc(total - baseUsd)} intl fee (${feePct}%)`} = <strong>{moneyc(total)}</strong></p>}
          <div className="form-row">
            <label className="field" style={{ flex: '1 1 100%' }}><span>Description *</span><input required value={f.description} onChange={set('description')} /></label>
            <label className="field"><span>Category</span>
              <select value={f.category_id} onChange={set('category_id')}><option value="">—</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
            <label className="field"><span>Vendor</span>
              <select value={f.vendor_id} onChange={set('vendor_id')}><option value="">—</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></label>
            <label className="field"><span>Status</span>
              <select value={f.status} onChange={set('status')}>{EXPENSE_STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
            <label className="field"><span>Recurrence</span>
              <select value={f.recurrence} onChange={set('recurrence')}>{RECURRENCES.map((r) => <option key={r} value={r}>{r === '' ? 'one-time' : r}</option>)}</select></label>
            <label className="field"><span>Invoice #</span><input value={f.invoice_number} onChange={set('invoice_number')} /></label>
            <label className="field"><span>Payment method</span><input value={f.payment_method} onChange={set('payment_method')} /></label>
          </div>
          <label className="field"><span>Notes</span><textarea className="textarea" value={f.notes} onChange={set('notes')} style={{ minHeight: 60 }} /></label>
          {editing && Number(e.attachment_count) > 0 && (
            <p className="small"><button type="button" className="link-pill" onClick={() => onView(e)}>View receipt ({Number(e.attachment_count)})</button> <span className="muted">— upload handled in legacy panel</span></p>
          )}
          <div className="row-actions" style={{ marginTop: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Add expense'}</button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════ inline forms ══════════════════════════ */
function SubForm({ initial, categories, vendors, onDone, onError, onCancel }:
  { initial?: Subscription; categories: Category[]; vendors: Vendor[]; onDone: (m: string) => void; onError: (e: any) => void; onCancel?: () => void }) {
  const editing = !!initial?.id;
  const seedCur = initial?.currency || 'USD';
  const seedAmt = seedCur !== 'USD' && initial?.native_amount != null ? initial.native_amount : initial?.amount_usd;
  const seedStatus = ['trial', 'active', 'paused'].includes(String(initial?.status)) ? String(initial?.status) : 'active';
  const { f, set, busy, setBusy } = useForm({
    name: initial?.name || '', started_on: initial?.started_on ? String(initial.started_on).slice(0, 10) : today(),
    billing_interval: initial?.billing_interval || 'monthly',
    amount: seedAmt != null ? String(seedAmt) : '', currency: seedCur,
    fx_rate: initial?.fx_rate != null ? String(initial.fx_rate) : '',
    intl_fee_pct: initial?.intl_fee_pct != null ? String(initial.intl_fee_pct) : '0',
    category_id: initial?.category_id || '', vendor_id: initial?.vendor_id || '', status: seedStatus,
  });
  const isUsd = f.currency === 'USD';
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    const body: any = {
      name: f.name.trim(), vendor_id: f.vendor_id || null, category_id: f.category_id || null,
      currency: f.currency, billing_interval: f.billing_interval, started_on: f.started_on,
      status: f.status, intl_fee_pct: f.intl_fee_pct || 0,
    };
    if (isUsd) { body.amount = f.amount; }
    else {
      const fx = parseFloat(f.fx_rate);
      if (!(fx > 0) || fx === 1) { onError({ message: `Enter the exchange rate for ${f.currency} (units per 1 USD).` }); setBusy(false); return; }
      body.native_amount = f.amount; body.fx_rate = f.fx_rate;
    }
    try {
      if (editing) await backendApi.put(`/license/admin/finances/subscriptions/${initial!.id}`, body);
      else await backendApi.post('/license/admin/finances/subscriptions', body);
      onDone(editing ? 'Subscription updated.' : 'Subscription added.');
    } catch (err) { onError(err); } finally { setBusy(false); }
  }
  return (
    <form className="inline-form" onSubmit={submit}>
      <div className="form-row">
        <label className="field"><span>Name *</span><input required value={f.name} onChange={set('name')} /></label>
        <label className="field"><span>Started *</span><input required type="date" value={f.started_on} onChange={set('started_on')} /></label>
        <label className="field"><span>Billing</span>
          <select value={f.billing_interval} onChange={set('billing_interval')}>{SUB_INTERVALS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select></label>
        <label className="field"><span>Amount *</span><input required type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} /></label>
        <label className="field"><span>Currency</span>
          <select value={f.currency} onChange={set('currency')}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
        {!isUsd && <label className="field"><span>FX ({f.currency}/USD) *</span><input type="number" min="0" step="0.0001" value={f.fx_rate} onChange={set('fx_rate')} placeholder="e.g. 280" /></label>}
        <label className="field"><span>Intl fee %</span><input type="number" min="0" step="0.01" value={f.intl_fee_pct} onChange={set('intl_fee_pct')} /></label>
        <label className="field"><span>Status</span>
          <select value={f.status} onChange={set('status')}>{SUB_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label className="field"><span>Vendor</span>
          <select value={f.vendor_id} onChange={set('vendor_id')}><option value="">—</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></label>
        <label className="field"><span>Category</span>
          <select value={f.category_id} onChange={set('category_id')}><option value="">—</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
      </div>
      <div className="row-actions">
        <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : editing ? 'Update subscription' : 'Add subscription'}</button>
        {editing && onCancel && <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

function FundingForm({ initial, onDone, onError, onCancel }: { initial?: Funding; onDone: (m: string) => void; onError: (e: any) => void; onCancel?: () => void }) {
  const editing = !!initial?.id;
  const { f, set, busy, setBusy } = useForm({
    received_on: initial?.received_on ? String(initial.received_on).slice(0, 10) : today(),
    source: initial?.source || '', amount: initial?.amount != null ? String(initial.amount) : '',
    note: initial?.note ?? initial?.notes ?? '',
  });
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    const body = { received_on: f.received_on, source: f.source.trim(), amount: f.amount, note: f.note || null };
    try {
      if (editing) await backendApi.put(`/license/admin/finances/funding/${initial!.id}`, body);
      else await backendApi.post('/license/admin/finances/funding', body);
      onDone(editing ? 'Funding updated.' : 'Funding added.');
    } catch (err) { onError(err); } finally { setBusy(false); }
  }
  return (
    <form className="inline-form" onSubmit={submit}>
      <div className="form-row">
        <label className="field"><span>Date *</span><input required type="date" value={f.received_on} onChange={set('received_on')} /></label>
        <label className="field"><span>Source *</span><input required value={f.source} onChange={set('source')} /></label>
        <label className="field"><span>Amount *</span><input required type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} /></label>
        <label className="field"><span>Notes</span><input value={f.note} onChange={set('note')} /></label>
      </div>
      <div className="row-actions">
        <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : editing ? 'Update funding' : 'Add funding'}</button>
        {editing && onCancel && <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

function CashForm({ onDone, onError }: { onDone: (m: string) => void; onError: (e: any) => void }) {
  const { f, set, busy, setBusy } = useForm({ month: thisMonth(), cash_balance: '', revenue_override: '' });
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    try {
      const body: any = { month: f.month, cash_balance: Number(f.cash_balance) };
      if (f.revenue_override !== '') body.revenue_override = Number(f.revenue_override);
      await backendApi.post('/license/admin/finances/cash', body);
      onDone('Cash balance updated.');
    } catch (err) { onError(err); } finally { setBusy(false); }
  }
  return (
    <form className="inline-form" onSubmit={submit}>
      <div className="form-row">
        <label className="field"><span>Month *</span><input required type="month" value={f.month} onChange={set('month')} /></label>
        <label className="field"><span>Cash balance *</span><input required type="number" min="0" step="0.01" value={f.cash_balance} onChange={set('cash_balance')} /></label>
        <label className="field"><span>Revenue override</span><input type="number" min="0" step="0.01" value={f.revenue_override} onChange={set('revenue_override')} placeholder="optional" /></label>
      </div>
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Update cash'}</button>
    </form>
  );
}

function BudgetForm({ onDone, onError }: { onDone: (m: string) => void; onError: (e: any) => void }) {
  const { f, set, busy, setBusy } = useForm({ label: '', amount: '', cost_type: 'fixed' });
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    try {
      await backendApi.post('/license/admin/finances/budget', { label: f.label.trim(), amount: Number(f.amount), cost_type: f.cost_type });
      onDone('Budget line added.');
    } catch (err) { onError(err); } finally { setBusy(false); }
  }
  return (
    <form className="inline-form" onSubmit={submit}>
      <div className="form-row">
        <label className="field"><span>Line item *</span><input required value={f.label} onChange={set('label')} /></label>
        <label className="field"><span>Amount / mo *</span><input required type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} /></label>
        <label className="field"><span>Type</span>
          <select value={f.cost_type} onChange={set('cost_type')}>{['fixed', 'variable'].map((c) => <option key={c}>{c}</option>)}</select></label>
      </div>
      <button className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Add line'}</button>
    </form>
  );
}
